import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { FaLock, FaLockOpen, FaMapMarkerAlt, FaTrash } from "react-icons/fa";
import classNames from "classnames";
import SubHeader from "components/Navbars/SubHeader";
import { getActiveColorStyle } from "utils/colorUtils";
import { getTheme2FormFieldsCss } from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import { resolveMediaUrl } from "utils/mediaUrl";
import { useNotification } from "contexts/NotificationContext";
import {
  useGetInventoryWebsite,
  useUpdateInventoryWebsite,
} from "api/inventory-website/service";
import WebsiteImagesTab from "./WebsiteImagesTab";

const FALLBACK_CAR_IMAGE = require("assets/img/car.png");

const JSON_TABS = [
  { key: "general_detail", label: "GENERAL" },
  { key: "pricing_detail", label: "PRICING" },
  { key: "comments", label: "COMMENTS" },
  { key: "images", label: "IMAGES" },
  { key: "equipment", label: "EQUIPMENT" },
  { key: "video", label: "VIDEO" },
  { key: "condition_report", label: "CONDITION REPORT" },
];

const REQUIRED_KEYS = new Set(["year", "make", "model"]);
const HIDDEN_KEYS = new Set(["document_id", "documentId", "documentID"]);
const READONLY_KEYS = new Set(["vin", "chrome_style_id", "dealer"]);
const BOOLEAN_KEYS = new Set([
  "certified",
  "dealer_certified",
  "is_reserved",
  "is_locked",
  "internet_special",
  "apply_price_rules",
  "apply_automated_rebates",
]);
const TYPE_KEYS = new Set(["type"]);

const formatLabel = (key) => {
  const map = {
    stock_number: "Stock #",
    chrome_style_id: "Chrome StyleID",
    document_id: "Document ID",
    date_in_stock: "Date In Stock",
    is_reserved: "Reserved",
    is_locked: "Locked",
    model_number: "Model Number",
    style_description: "Style Description",
    transmission_speed: "Transmission Speed",
    transmission_description: "Transmission Description",
    engine_cylinders: "Engine Cylinders",
    engine_displacement: "Engine Displacement",
    engine_block_type: "Engine Block Type",
    engine_aspiration_type: "Engine Aspiration Type",
    engine_description: "Engine Description",
    fuel_type: "Fuel Type",
    wheelbase_code: "Wheelbase Code",
    exterior_color: "Exterior Color",
    interior_color: "Interior Color",
    int_upholstery: "Int. Upholstery",
    epa_city: "EPA City",
    epa_highway: "EPA Highway",
    selling_price: "Selling Price",
    invoice_amount: "Invoice Amount",
    rebate_amount: "Rebate Amount",
    blue_book: "Blue Book",
    internet_price: "Internet Price",
    misc_price1: "Misc Price 1",
    misc_price2: "Misc Price 2",
    misc_price3: "Misc Price 3",
    internet_special: "Internet Special",
    apply_price_rules: "Apply Price Rules",
    apply_automated_rebates: "Apply Automated Rebates",
    main_comments: "Main Comments",
    comment_1: "Comment 1",
    comment_2: "Comment 2",
    comment_3: "Comment 3",
    comment_4: "Comment 4",
    comment_5: "Comment 5",
    dealer_certified: "Dealer Certified",
    standard_body: "Standard Body",
  };
  if (map[key]) return map[key];
  return String(key || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatAddedDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const daysSince = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const today = new Date();
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diff = Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
  return diff;
};

const asCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const normalizeImages = (images) => {
  if (!Array.isArray(images)) return [];
  return images
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        return item.url || item.src || item.thumbnail_url || item.file_url || null;
      }
      return null;
    })
    .filter(Boolean);
};

const InventoryWebsiteDetail = ({
  activeColor = "blue",
  shellThemeVariant = "1",
  isLightTheme = false,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const isTheme2 = shellThemeVariant === "2";
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".inventory-website-detail-fields",
          })
        : "",
    [isTheme2]
  );
  const inputClassName = "form-control";
  const inputStyle = isTheme2
    ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
    : { fontSize: "14px" };

  const { data, isLoading, isError } = useGetInventoryWebsite(id);
  const updateMutation = useUpdateInventoryWebsite();

  const [activeTab, setActiveTab] = useState("GENERAL");
  const [drafts, setDrafts] = useState({});
  const [removeOpen, setRemoveOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!data) return;
    setDrafts({
      general_detail: { ...(data.general_detail || {}) },
      pricing_detail: { ...(data.pricing_detail || {}) },
      comments: { ...(data.comments || {}) },
      images: Array.isArray(data.images) ? [...data.images] : [],
      equipment: Array.isArray(data.equipment)
        ? data.equipment.map((item) => ({ ...item }))
        : data.equipment && typeof data.equipment === "object"
          ? { ...data.equipment }
          : [],
      video: data.video && typeof data.video === "object" ? { ...data.video } : data.video || {},
      condition_report:
        data.condition_report && typeof data.condition_report === "object"
          ? { ...data.condition_report }
          : data.condition_report || {},
    });
  }, [data]);

  const general = drafts.general_detail || {};
  const images = normalizeImages(drafts.images);
  const videoCount = Array.isArray(drafts.video)
    ? drafts.video.length
    : drafts.video && typeof drafts.video === "object" && Object.keys(drafts.video).length > 0
      ? 1
      : 0;

  const title = [general.year, general.make, general.model, general.trim]
    .filter(Boolean)
    .join(" ");

  const activeFieldKey = JSON_TABS.find((tab) => tab.label === activeTab)?.key || "general_detail";
  const activeDraft = drafts[activeFieldKey];

  const setFieldValue = (fieldKey, key, value) => {
    setDrafts((prev) => ({
      ...prev,
      [fieldKey]: {
        ...(prev[fieldKey] && typeof prev[fieldKey] === "object" && !Array.isArray(prev[fieldKey])
          ? prev[fieldKey]
          : {}),
        [key]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (!id || data?.is_locked) return;
    setIsSaving(true);
    try {
      await updateMutation.mutateAsync({
        id,
        data: {
          general_detail: drafts.general_detail,
          pricing_detail: drafts.pricing_detail,
          comments: drafts.comments,
          images: drafts.images,
          equipment: drafts.equipment,
          video: drafts.video,
          condition_report: drafts.condition_report,
        },
      });
      showNotification("success", t("inventoryWebsite.saveSuccess"));
    } catch (error) {
      showNotification("error", t("inventoryWebsite.saveError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleLock = async () => {
    if (!id) return;
    try {
      await updateMutation.mutateAsync({
        id,
        data: { is_locked: !data?.is_locked },
      });
      showNotification(
        "success",
        data?.is_locked
          ? t("inventoryWebsite.unlockSuccess")
          : t("inventoryWebsite.lockSuccess")
      );
    } catch (error) {
      showNotification("error", t("inventoryWebsite.lockError"));
    }
  };

  const handleRemove = async () => {
    if (!id) return;
    try {
      await updateMutation.mutateAsync({ id, data: { is_active: false } });
      setRemoveOpen(false);
      showNotification("success", t("inventoryWebsite.removeSuccess"));
      navigate("/inventory-website");
    } catch (error) {
      showNotification("error", t("inventoryWebsite.removeError"));
    }
  };

  const renderObjectFields = (fieldKey, obj) => {
    const entries = Object.entries(obj || {}).filter(
      ([key]) => !HIDDEN_KEYS.has(key) && !HIDDEN_KEYS.has(String(key).toLowerCase())
    );
    if (entries.length === 0) {
      return (
        <div className="text-muted py-4 text-center">
          {t("inventoryWebsite.emptyTab")}
        </div>
      );
    }

    return (
      <div className="inventory-website-detail-fields card p-3">
        {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
        <div className="inventory-website-detail-grid">
        {entries.map(([key, value]) => {
          const isReadonly = READONLY_KEYS.has(key) || data?.is_locked;
          const label = formatLabel(key);
          const requiredMark = REQUIRED_KEYS.has(key) ? " *" : "";
          const isWide =
            key.includes("comment") ||
            key.includes("description") ||
            (typeof value === "string" && value.length > 80);

          let control = null;
          if (TYPE_KEYS.has(key)) {
            const current = String(value || "").toLowerCase();
            control = (
              <div className="d-flex align-items-center" style={{ gap: 16 }}>
                {["New", "Used"].map((option) => (
                  <label key={option} className="mb-0 d-flex align-items-center" style={{ gap: 6 }}>
                    <input
                      type="radio"
                      name={`${fieldKey}-${key}`}
                      checked={current === option.toLowerCase()}
                      disabled={isReadonly}
                      onChange={() => setFieldValue(fieldKey, key, option)}
                      style={{ accentColor: activeColorStyle.borderColor }}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            );
          } else if (BOOLEAN_KEYS.has(key) || typeof value === "boolean") {
            const boolVal = value === true || value === "true" || value === "Yes";
            control = (
              <div className="d-flex align-items-center" style={{ gap: 16 }}>
                {[
                  { label: "Yes", value: true },
                  { label: "No", value: false },
                ].map((option) => (
                  <label
                    key={option.label}
                    className="mb-0 d-flex align-items-center"
                    style={{ gap: 6 }}
                  >
                    <input
                      type="radio"
                      name={`${fieldKey}-${key}`}
                      checked={boolVal === option.value}
                      disabled={isReadonly}
                      onChange={() => setFieldValue(fieldKey, key, option.value)}
                      style={{ accentColor: activeColorStyle.borderColor }}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            );
          } else if (isReadonly) {
            control = (
              <div style={{ padding: "6px 0", fontSize: inputStyle.fontSize }}>
                {value == null || value === "" ? "—" : String(value)}
              </div>
            );
          } else if (isWide) {
            control = (
              <Input
                className={inputClassName}
                style={inputStyle}
                type="textarea"
                rows={3}
                value={value ?? ""}
                onChange={(e) => setFieldValue(fieldKey, key, e.target.value)}
              />
            );
          } else {
            control = (
              <Input
                className={inputClassName}
                style={inputStyle}
                type="text"
                value={value ?? ""}
                onChange={(e) => setFieldValue(fieldKey, key, e.target.value)}
              />
            );
          }

          return (
            <div
              key={key}
              className={
                isWide
                  ? "inventory-website-detail-row inventory-website-detail-row--wide"
                  : "inventory-website-detail-row"
              }
            >
              <div className="inventory-website-detail-label">
                {label}
                {requiredMark}
              </div>
              <div className="inventory-website-detail-control">{control}</div>
            </div>
          );
        })}
        </div>
      </div>
    );
  };

  const renderImagesTab = () => (
    <WebsiteImagesTab
      websiteId={id}
      images={drafts.images}
      isLocked={Boolean(data?.is_locked)}
      activeColor={activeColor}
      onImagesChange={(nextImages) => {
        setDrafts((prev) => ({ ...prev, images: nextImages }));
      }}
    />
  );

  const renderEquipmentTab = () => {
    const equipment = drafts.equipment;
    const list = Array.isArray(equipment) ? equipment : [];
    if (list.length === 0) {
      return (
        <div className="card p-4 text-muted text-center">{t("inventoryWebsite.emptyTab")}</div>
      );
    }
    return (
      <div className="inventory-website-detail-fields card p-3">
        {list.map((item, index) => (
          <div key={`${item.key || index}`} className="inventory-website-detail-row">
            <div className="inventory-website-detail-label">
              {item.type ? formatLabel(item.type) : `Item ${index + 1}`}
            </div>
            <div className="inventory-website-detail-control">
              {item.label || item.key || "—"}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTabBody = () => {
    if (activeFieldKey === "images") return renderImagesTab();
    if (activeFieldKey === "equipment") return renderEquipmentTab();
    if (Array.isArray(activeDraft)) {
      return (
        <div className="card p-4 text-muted text-center">{t("inventoryWebsite.emptyTab")}</div>
      );
    }
    return renderObjectFields(activeFieldKey, activeDraft || {});
  };

  if (isLoading) {
    return (
      <div
        className="content"
        style={{
          minHeight: "80vh",
          paddingTop: isMobile ? "7rem" : null,
          position: "relative",
        }}
      >
        <div className="d-flex justify-content-center align-items-center py-5">
          <div
            className="spinner-border"
            role="status"
            style={{ color: activeColorStyle.borderColor }}
          />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div
        className="content"
        style={{
          minHeight: "80vh",
          paddingTop: isMobile ? "7rem" : null,
          position: "relative",
        }}
      >
        <div style={{ width: "100%", position: "relative", zIndex: 1 }}>
          <SubHeader
            activeColor={activeColor}
            title={t("inventoryWebsite.detailTitle")}
            back={-1}
            backFallback="/inventory-website"
            theme2ScrollGapFill={isTheme2}
            isLightTheme={isLightTheme}
          />
          <div className="card p-4 text-center text-muted">
            {t("inventoryWebsite.notFound")}
          </div>
        </div>
      </div>
    );
  }

  const heroImageSrc = images[0]
    ? resolveMediaUrl(images[0]) || images[0]
    : FALLBACK_CAR_IMAGE;

  return (
    <div
      className={classNames("content inventory-website-detail-page", {
        "inventory-website-detail-page--theme2": isTheme2,
      })}
      style={{
        minHeight: "80vh",
        paddingTop: isMobile ? "7rem" : null,
        position: "relative",
      }}
    >
      <div style={{ width: "100%", position: "relative", zIndex: 1 }}>
        <div className="inventory-website-detail-subheader-wrap">
          <SubHeader
            activeColor={activeColor}
            title={title || t("inventoryWebsite.detailTitle")}
            back={-1}
            backFallback="/inventory-website"
            status={general.status || null}
            theme2ScrollGapFill={isTheme2}
            isLightTheme={isLightTheme}
          />
        </div>

        <div className="card mb-3 inventory-website-detail-hero">
        <div className="d-flex flex-column flex-lg-row" style={{ gap: 16 }}>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="d-flex flex-column flex-sm-row" style={{ gap: 16 }}>
              <div
                style={{
                  width: isMobile ? "100%" : 220,
                  flexShrink: 0,
                  background: "#f3f4f6",
                  overflow: "hidden",
                  border: "1px solid #e5e7eb",
                }}
              >
                <img
                  src={heroImageSrc}
                  alt={title || "Vehicle"}
                  style={{
                    width: "100%",
                    height: isMobile ? 150 : 130,
                    objectFit: images[0] ? "cover" : "contain",
                    display: "block",
                    padding: images[0] ? 0 : 12,
                    background: "#f3f4f6",
                  }}
                />
                <div
                  className="d-flex align-items-center justify-content-center text-white"
                  style={{
                    background: "#4b5563",
                    padding: "5px 10px",
                    fontSize: 12,
                    fontWeight: 600,
                    gap: 16,
                  }}
                >
                  <span>
                    {t("inventoryWebsite.photoCount", { count: images.length })}
                  </span>
                  <span>
                    {t("inventoryWebsite.videoCount", { count: videoCount })}
                  </span>
                </div>
              </div>

              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <h5 className="mb-1 font-weight-bold">{title || "—"}</h5>
                <div className="text-muted mb-1" style={{ fontSize: 14 }}>
                  {t("inventoryWebsite.stock")} {general.stock_number || "—"}
                </div>
                {general.dealer && (
                  <div className="d-flex align-items-center mb-1" style={{ gap: 6, fontSize: 14 }}>
                    <FaMapMarkerAlt size={12} />
                    <span>{general.dealer}</span>
                  </div>
                )}
                {data.add_date && (
                  <div
                    className="d-flex align-items-center flex-wrap mb-2"
                    style={{ gap: 8, fontSize: 13 }}
                  >
                    <span className="text-muted">
                      {t("inventoryWebsite.added", {
                        date: formatAddedDate(data.add_date),
                      })}
                    </span>
                    {daysSince(data.add_date) != null && (
                      <span
                        style={{
                          background: "#22c55e",
                          color: "#fff",
                          padding: "1px 8px",
                          fontSize: 12,
                          fontWeight: 700,
                          lineHeight: "18px",
                        }}
                      >
                        {daysSince(data.add_date)}d
                      </span>
                    )}
                  </div>
                )}

                <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                  <div
                    style={{
                      background: "#0d9488",
                      color: "#fff",
                      padding: "8px 12px",
                      minWidth: 96,
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: 11, opacity: 0.9 }}>
                      {t("inventoryWebsite.viewCount")}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>
                      {asCount(data.view_count)}
                    </div>
                  </div>
                  <div
                    style={{
                      background: "#f59e0b",
                      color: "#fff",
                      padding: "8px 12px",
                      minWidth: 96,
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: 11, opacity: 0.9 }}>
                      {t("inventoryWebsite.viewDetailCount")}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>
                      {asCount(data.view_detail_count)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="d-flex flex-row flex-lg-column align-items-center align-items-lg-end"
            style={{ gap: 8, flexShrink: 0 }}
          >
            <Button
              color={data.is_locked ? "warning" : "secondary"}
              outline
              size="sm"
              className="d-inline-flex align-items-center"
              style={{ gap: 6, minWidth: 110 }}
              onClick={handleToggleLock}
              disabled={updateMutation.isPending}
            >
              {data.is_locked ? <FaLockOpen size={12} /> : <FaLock size={12} />}
              {data.is_locked
                ? t("inventoryWebsite.unlock")
                : t("inventoryWebsite.lock")}
            </Button>
            <Button
              color="danger"
              outline
              size="sm"
              className="d-inline-flex align-items-center"
              style={{ gap: 6, minWidth: 110 }}
              onClick={() => setRemoveOpen(true)}
            >
              <FaTrash size={12} />
              {t("inventoryWebsite.remove")}
            </Button>
          </div>
        </div>
      </div>

      <div
        className={
          isMobile
            ? "w-100 search-detail-tabs detail-mobile-tabs"
            : "w-100 search-detail-tabs"
        }
      >
        <div
          className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between"
          style={{ gap: 12 }}
        >
          <Nav
            tabs
            className="border-0 flex-grow-1"
            style={{
              borderBottom: "none",
              display: "flex",
              flexWrap: isMobile ? "nowrap" : "wrap",
              overflowX: isMobile ? "auto" : "visible",
              paddingLeft: 0,
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            {JSON_TABS.map((tab) => (
              <NavItem key={tab.label} style={{ flex: "0 0 auto" }}>
                <NavLink
                  href="#"
                  className={activeTab === tab.label ? "active font-weight-bold" : "text-muted"}
                  style={{
                    background: "none",
                    border: "none",
                    position: "relative",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    color:
                      activeTab === tab.label ? activeColorStyle.borderColor : undefined,
                    fontWeight: activeTab === tab.label ? 700 : 500,
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab(tab.label);
                  }}
                >
                  {tab.label}
                  {activeTab === tab.label && (
                    <div
                      style={{
                        height: 3,
                        background: activeColorStyle.borderColor,
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: -8,
                      }}
                    />
                  )}
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          {!data.is_locked && (
            <div className="flex-shrink-0 d-flex justify-content-end align-items-center pl-2">
              <button
                type="button"
                className="btn btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ ...activeColorStyle, color: "white", cursor: "pointer", margin: 0 }}
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? t("inventoryWebsite.saving") : t("inventoryWebsite.save")}
              </button>
            </div>
          )}
        </div>
      </div>

      <TabContent activeTab={activeTab} className="inventory-website-detail-tab-content">
        <TabPane tabId={activeTab}>{renderTabBody()}</TabPane>
      </TabContent>

      <Modal isOpen={removeOpen} toggle={() => setRemoveOpen(false)} centered>
        <ModalBody>
          {t("inventoryWebsite.removeConfirm", {
            name: title || general.stock_number || id,
          })}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setRemoveOpen(false)}>
            {t("inventoryWebsite.cancel")}
          </Button>
          <Button
            color="danger"
            onClick={handleRemove}
            disabled={updateMutation.isPending}
          >
            {t("inventoryWebsite.remove")}
          </Button>
        </ModalFooter>
      </Modal>

      <style>{`
        /* Keep page inset like inventory detail — do not let SubHeader go full-bleed to viewport edge */
        .main-panel > .content.inventory-website-detail-page {
          padding-right: 30px !important;
          box-sizing: border-box;
        }
        .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.inventory-website-detail-page {
          padding-right: 20px !important;
        }
        @media (max-width: 767.98px) {
          .main-panel > .content.inventory-website-detail-page {
            padding-right: 15px !important;
            padding-left: 15px !important;
          }
        }
        .inventory-website-detail-subheader-wrap {
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }
        .inventory-website-detail-subheader-wrap > div {
          position: sticky !important;
          left: auto !important;
          right: auto !important;
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box;
        }
        .inventory-website-detail-hero {
          padding: 12px;
        }
        .inventory-website-detail-page .card,
        .inventory-website-detail-page .card-img-top,
        .inventory-website-detail-page .card-header,
        .inventory-website-detail-page .card-body,
        .inventory-website-detail-page .btn,
        .inventory-website-detail-page img {
          border-radius: 0 !important;
        }
        .inventory-website-detail-tab-content {
          margin-top: 0 !important;
          padding-top: 0 !important;
        }
        .inventory-website-detail-tab-content > .tab-pane {
          padding-top: 0;
        }
        .inventory-website-detail-fields .form-control:focus {
          border-color: ${activeColorStyle.borderColor} !important;
          box-shadow: none !important;
        }
        .inventory-website-detail-fields.card {
          margin-top: 0;
        }
        .inventory-website-detail-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          column-gap: 24px;
          row-gap: 0;
        }
        .inventory-website-detail-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 0;
          border-bottom: 1px solid #eef0f3;
          min-width: 0;
        }
        .inventory-website-detail-row--wide {
          grid-column: 1 / -1;
        }
        .inventory-website-detail-label {
          width: 130px;
          flex-shrink: 0;
          color: #374151;
          font-size: 14px;
        }
        .inventory-website-detail-control {
          flex: 1;
          min-width: 0;
        }
        @media (max-width: 991px) {
          .inventory-website-detail-grid {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 767px) {
          .inventory-website-detail-row {
            flex-direction: column;
            align-items: stretch;
            gap: 6px;
          }
          .inventory-website-detail-label {
            width: 100%;
          }
        }
      `}</style>
      </div>
    </div>
  );
};

export default InventoryWebsiteDetail;
